import { useEffect, useRef } from "react";

/* One polling hook for every screen timer.
 *
 *   usePolling(fn, 5000);                       // run now, then every 5 s
 *   usePolling(fn, 1500, { enabled: !!key });   // off until `key` exists
 *
 * The interval only runs while the document is VISIBLE. A hidden tab (another
 * tab in front, a minimised window) clears the timer; the moment the tab comes
 * back the callback runs once immediately and the cadence restarts. Before this
 * every open tab kept ~2 requests/s flowing to the backend for nothing.
 *
 * `fn` is read through a ref, so callers need not memoise it and the timer is
 * never torn down just because a render produced a new closure. The timer IS
 * restarted when `ms` or `enabled` change.
 */
export function isDocumentHidden() {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

export function usePolling(fn, ms, { enabled = true, immediate = true } = {}) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled || !(ms > 0)) return undefined;
    let timer = null;
    let alive = true;
    const run = () => { if (alive) fnRef.current?.(); };
    const start = () => {
      if (timer || !alive) return;
      timer = setInterval(run, ms);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (isDocumentHidden()) stop();
      else { run(); start(); }
    };
    if (!isDocumentHidden()) {
      if (immediate) run();
      start();
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive = false;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [ms, enabled, immediate]);
}

export default usePolling;
